import {addUrlToHistoryStack} from '@github-ui/history'
import {ssrSafeLocation} from '@github-ui/ssr-utils'
import {useCallback, useMemo, useRef, useSyncExternalStore} from 'react'

// Mirrored to the URL so the panel is shareable and follows back/forward.
const TASK_PARAM = 'agent_task'
const REPO_PARAM = 'agent_task_repo_id'
const ISSUE_PARAM = 'agent_task_issue'

/** The session to open, and the issue whose sessions the panel lists. */
export type AgentSessionPanelTarget = {
  taskId: string
  repoId: number
  owner: string
  repo: string
  number: number
}

const listeners = new Set<() => void>()

function subscribe(listener: () => void) {
  listeners.add(listener)
  window.addEventListener('popstate', listener)
  return () => {
    listeners.delete(listener)
    window.removeEventListener('popstate', listener)
  }
}

function getSnapshot() {
  const params = new URLSearchParams(ssrSafeLocation.search)
  return [params.get(TASK_PARAM), params.get(REPO_PARAM), params.get(ISSUE_PARAM)].join('\n')
}

const getServerSnapshot = () => ''

function parse(snapshot: string): AgentSessionPanelTarget | null {
  const [taskId, rawRepoId, issue] = snapshot.split('\n')
  const [owner, repo, rawNumber] = issue?.split('/') ?? []
  const repoId = Number(rawRepoId)
  const number = Number(rawNumber)
  if (!taskId || !owner || !repo || !Number.isInteger(repoId) || !Number.isInteger(number)) return null

  return {taskId, repoId, owner, repo, number}
}

function write(target: AgentSessionPanelTarget | null) {
  const url = new URL(ssrSafeLocation.href, ssrSafeLocation.origin)
  if (target) {
    url.searchParams.set(TASK_PARAM, target.taskId)
    url.searchParams.set(REPO_PARAM, String(target.repoId))
    url.searchParams.set(ISSUE_PARAM, `${target.owner}/${target.repo}/${target.number}`)
  } else {
    // Closing a closed panel adds no history entry.
    if (![TASK_PARAM, REPO_PARAM, ISSUE_PARAM].some(param => url.searchParams.has(param))) return
    url.searchParams.delete(TASK_PARAM)
    url.searchParams.delete(REPO_PARAM)
    url.searchParams.delete(ISSUE_PARAM)
  }
  addUrlToHistoryStack(url.toString())
  for (const listener of listeners) listener()
}

export function useAgentSessionPanel() {
  const snapshot = useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot)
  const target = useMemo(() => parse(snapshot), [snapshot])
  const returnFocusRef = useRef<HTMLElement | null>(null)

  const open = useCallback((next: AgentSessionPanelTarget, returnFocusElement: HTMLElement | null) => {
    returnFocusRef.current = returnFocusElement
    write(next)
  }, [])

  const close = useCallback(() => write(null), [])

  return {target, open, close, returnFocusRef}
}
